import React from 'react';
import OmInputFieldWrapper from '../forms/OmInputFieldWrapper';
import { createActionHandler, roundCurrency } from '../../util';
import ResultCard from './ResultCardSection';
import {
    setGenerateRegularIncomeOnceOffValue,
    setMonthlyValue,
    setMoneyMarketFundPercentValue,
    setBondFundPercentValue,
    calculateGenerateRegularIncome,
    deselectEvenSplit,
    selectEvenSplit,
} from '@om/redux/actions/investmentCalculator/generateRegularIncome';

import {
    OmInvestmentCalcSection,
    OmApplicationFundSplitList,
    OmApplicationFundSplitCard,
} from '@om/component-library-react';

import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
    getAmountOnceOffValue,
    getMonthlyAmountValue,
    getCalculateDisabled,
    getMoneyMarketFundPercentValue,
    getBondFundPercentValue,
    getMoneyMarketFundSplitValue,
    getBondFundSplitValue,
    getIsEvenSplit,
    getSplitError,
    getTotalPercentage,
} from '@om/redux/selectors/investmentCalculator/generateRegularIncomeCalculator';

import { getShowRecommendation } from '@om/redux/selectors/investmentCalculator/investmentCalculator';

import TwoColumnImageLeft from '../TwoColumnImageLeft';
import TwoColumnImageRight from '../TwoColumnImageRight';

const showAddMoreFundsButton = false;
const hideDeleteButton = true;
const fundOne = false;

const RegularIncomeCalculator = ({
    slot,
    twoColumnImageLeftContent,
    twoColumnImageRightContent,
}) => {
    const dispatch = useDispatch();

    const {
        amountOnceOff,
        monthlyAmount,
        calculateButtonDisabled,
        moneyMarketFundPercent,
        bondFundPercent,
        moneyMarketFundSplit,
        bondFundSplit,
        totalPercentage,
        isEvenSplit,
        splitError,
        showRecommendation,
    } = useSelector(
        createStructuredSelector({
            amountOnceOff: getAmountOnceOffValue,
            monthlyAmount: getMonthlyAmountValue,
            moneyMarketFundPercent: getMoneyMarketFundPercentValue,
            moneyMarketFundSplit: getMoneyMarketFundSplitValue,
            bondFundPercent: getBondFundPercentValue,
            bondFundSplit: getBondFundSplitValue,
            calculateButtonDisabled: getCalculateDisabled,
            totalPercentage: getTotalPercentage,
            isEvenSplit: getIsEvenSplit,
            splitError: getSplitError,
            showRecommendation: getShowRecommendation,
        }),
    );

    const customActionHandler = (dispatch, action, correspondingAction) => {
        if (!dispatch || !action) {
            return () => {};
        }

        return value => {
            //Set the corresponding fund value to 100 - x
            dispatch(correspondingAction((100 - Number(value)).toString()));
            //Set the fund value that is currently being edited
            dispatch(action(value.toString()));
            if (value !== 50) dispatch(deselectEvenSplit());
            else dispatch(selectEvenSplit());
        };
    };

    return (
        <div slot={slot}>
            <OmInvestmentCalcSection>
                <div slot='form-content'>
                    <h4>Invest to generate regular income</h4>
                    <h6 className='no-bottom-margin'>
                        How much do you want to invest once-off?
                    </h6>
                    <OmInputFieldWrapper
                        isSensitive={true}
                        preset='KES'
                        prefixString='KES'
                        required={true}
                        size='full'
                        formId='GenerateRegularIncomeCalculatorForm'
                        name='amountOnceOff'
                        mask='thousands-spaced'
                        keyboardMode='numeric'
                        hintText=''
                        reduxValue={amountOnceOff}
                        persistHandler={createActionHandler(
                            dispatch,
                            setGenerateRegularIncomeOnceOffValue,
                        )}
                    />
                    <h6 className='no-bottom-margin'>
                        How much do you want to save monthly?
                    </h6>
                    <OmInputFieldWrapper
                        isSensitive={true}
                        preset='KES'
                        prefixString='KES'
                        required={true}
                        size='full'
                        formId='GenerateRegularIncomeCalculatorForm'
                        name='amountMonthly'
                        mask='thousands-spaced'
                        keyboardMode='numeric'
                        hintText=''
                        align='left'
                        reduxValue={monthlyAmount}
                        persistHandler={createActionHandler(
                            dispatch,
                            setMonthlyValue,
                        )}
                    />
                    <div className='split-form'>
                        <h5>
                            Split your <strong>investment between funds</strong>
                        </h5>
                        <OmApplicationFundSplitList
                            checkboxId='generate-regular-income-regular-checkbox'
                            onCheckBoxChanged={() => {
                                if (isEvenSplit) {
                                    dispatch(deselectEvenSplit());
                                    return;
                                }
                                dispatch(selectEvenSplit());
                            }}
                            disableCheckBox={false}
                            evenSplit={isEvenSplit}
                            totalPercentage={totalPercentage}
                            showAddMoreFundsButton={showAddMoreFundsButton}
                            totalValue={
                                'KES ' +
                                roundCurrency(
                                    moneyMarketFundSplit + bondFundSplit,
                                )
                            }>
                            <div slot='fund-cards'>
                                <OmApplicationFundSplitCard
                                    fundName={'Money Market Fund'}
                                    value={
                                        'KES ' +
                                        roundCurrency(moneyMarketFundSplit)
                                    }
                                    hideDeleteButton={hideDeleteButton}>
                                    <OmInputFieldWrapper
                                        align='left'
                                        type={'number'}
                                        maxLength={3}
                                        slot='percentage-input'
                                        required={true}
                                        size='small'
                                        formId='GenerateRegularIncomeCalculatorForm'
                                        name='moneyMarketFundPercent'
                                        keyboardMode='numeric'
                                        hintText=''
                                        disabled={isEvenSplit}
                                        reduxValue={moneyMarketFundPercent}
                                        persistHandler={customActionHandler(
                                            dispatch,
                                            setMoneyMarketFundPercentValue,
                                            setBondFundPercentValue,
                                        )}
                                    />
                                </OmApplicationFundSplitCard>
                                <OmApplicationFundSplitCard
                                    fundName={'Bond Fund'}
                                    errorMessage={splitError}
                                    value={
                                        'KES ' + roundCurrency(bondFundSplit)
                                    }
                                    hideDeleteButton={hideDeleteButton}
                                    fundOne={fundOne}>
                                    <OmInputFieldWrapper
                                        align='left'
                                        type={'number'}
                                        maxLength={3}
                                        slot='percentage-input'
                                        required={true}
                                        size='small'
                                        formId='GenerateRegularIncomeCalculatorForm'
                                        name='bondFundPercent'
                                        keyboardMode='numeric'
                                        hintText=''
                                        disabled={isEvenSplit}
                                        reduxValue={bondFundPercent}
                                        persistHandler={customActionHandler(
                                            dispatch,
                                            setBondFundPercentValue,
                                            setMoneyMarketFundPercentValue,
                                        )}
                                    />
                                </OmApplicationFundSplitCard>
                            </div>
                        </OmApplicationFundSplitList>
                    </div>
                </div>
                <ResultCard
                    calculateButtonDisabled={calculateButtonDisabled}
                    setInvestmentResult={() =>
                        dispatch(calculateGenerateRegularIncome())
                    }
                />

                <div slot='product-offering'>
                    {showRecommendation && (
                        <>
                            <TwoColumnImageLeft
                                twoColumnImageLeftContent={
                                    twoColumnImageLeftContent
                                }
                            />
                            <TwoColumnImageRight
                                twoColumnImageRightContent={
                                    twoColumnImageRightContent
                                }
                            />
                        </>
                    )}
                </div>
            </OmInvestmentCalcSection>
        </div>
    );
};

export default RegularIncomeCalculator;
